import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Spin } from "antd";
import { fetchScheduleById } from "./scheduleApi";
import { mergeScheduleRecords, rowFromSchedule, NOT_RETURNED } from "./scheduleMappers";
import { upcomingRunDates } from "./expandRecurrence";
import "./ScheduleSummaryPanel.css";

/**
 * The summary side panel behind the eye icon on the Scheduled Reports list.
 *
 * Built on antd's Drawer, which already does the container work — portal, mask,
 * slide, mask-click and Escape to close, body scroll lock, and a `footer` slot
 * for the Edit button. Only the contents are ours: the field rows, the status
 * badge and the upcoming-schedules calendar. Padding and rules come through
 * headerStyle / bodyStyle / footerStyle rather than by overriding
 * .ant-drawer-* class names.
 *
 * ── IT FETCHES ITS OWN DETAIL ───────────────────────────────────────────────
 * The list hands over a row built from GET /v1/scheduler/all, which is a reduced
 * projection — its Timezone comes back as an empty string, so Time Zone showed a
 * dash for every schedule. GET /v1/scheduler/id carries the real value, plus the
 * ValidTo / ExecutionLimit / ScheduleEndCondition a legacy schedule's end date
 * lives in.
 *
 * The fetch is here rather than in the list page because this component already
 * owns the awkward part of the lifecycle — see useLastSchedule below — and
 * nothing else wants the full record. The table has no Time Zone column.
 *
 * ── AND WORKS OUT ITS OWN RUN DATES ─────────────────────────────────────────
 * Run days used to be mock values on the row, with a note saying the API should
 * return them. It does not, and cannot cheaply: RunTime always comes back as the
 * zero timestamp. They are derived from the saved recurrence instead — see
 * expandRecurrence.js.
 */

const Icon = {
  close: (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 20 20" width="15" height="15" fill="none" aria-hidden="true">
      <rect x="2.5" y="4" width="15" height="13.5" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  chevronLeft: (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
      <path d="M10 3.5L5.5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chevronRight: (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
      <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M11.2 2.3l2.5 2.5-8 8-3.2.7.7-3.2 8-8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
};

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* Days are compared as "YYYY-MM-DD" strings rather than as Date objects: a
   run day is a calendar day, and turning it into a Date shifts it across a
   timezone boundary for anyone east or west of where it was written. */
const pad2 = (n) => String(n).padStart(2, "0");
const toDayKey = (year, month, day) => `${year}-${pad2(month + 1)}-${pad2(day)}`;

const todayKey = () => {
  const now = new Date();
  return toDayKey(now.getFullYear(), now.getMonth(), now.getDate());
};

/* The month to open on: the first run day that has not passed, else the first
   one at all, else this month. Opening on an empty month would look broken. */
function initialMonth(upcomingDates) {
  const dates = (upcomingDates || []).slice().sort();
  const today = todayKey();
  const target = dates.find((d) => d >= today) || dates[0];
  if (target) {
    const [y, m] = target.split("-");
    return { year: Number(y), month: Number(m) - 1 };
  }
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
}

function CalendarMonth({ upcomingDates }) {
  const [view, setView] = useState(() => initialMonth(upcomingDates));

  // A different schedule means a different set of run days to centre on.
  useEffect(() => {
    setView(initialMonth(upcomingDates));
  }, [upcomingDates]);

  const scheduled = useMemo(() => new Set(upcomingDates || []), [upcomingDates]);
  const today = todayKey();

  const { year, month } = view;
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) {
    cells.push({ key: `blank-${i}`, blank: true });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dayKey = toDayKey(year, month, day);
    cells.push({
      key: dayKey,
      day,
      isScheduled: scheduled.has(dayKey),
      isToday: dayKey === today,
    });
  }

  const step = (delta) => {
    setView((v) => {
      const next = new Date(v.year, v.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  return (
    <div className="sspanel-calendar">
      <div className="sspanel-cal-head">
        <button type="button" className="sspanel-cal-nav" onClick={() => step(-1)} aria-label="Previous month">
          {Icon.chevronLeft}
        </button>
        <span className="sspanel-cal-month">
          {MONTH_LABELS[month]} {year}
        </span>
        <button type="button" className="sspanel-cal-nav" onClick={() => step(1)} aria-label="Next month">
          {Icon.chevronRight}
        </button>
      </div>
      <div className="sspanel-cal-grid">
        {WEEKDAY_LABELS.map((label, i) => (
          <div className="sspanel-cal-dow" key={`dow-${i}`}>
            {label}
          </div>
        ))}
        {cells.map((cell) =>
          cell.blank ? (
            <div className="sspanel-cal-day sspanel-cal-day--blank" key={cell.key} />
          ) : (
            <div
              className={[
                "sspanel-cal-day",
                cell.isScheduled ? "sspanel-cal-day--scheduled" : "",
                cell.isToday ? "sspanel-cal-day--today" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              key={cell.key}
              title={cell.isScheduled ? "Scheduled to run" : undefined}
            >
              <span>{cell.day}</span>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

/* Drawer keeps its children mounted while it slides out, but the list drops its
   selected row the moment the panel closes — so the last one is held here or
   the panel empties out mid-slide. */
function useLastSchedule(schedule) {
  const [shown, setShown] = useState(schedule);
  useEffect(() => {
    if (schedule) setShown(schedule);
  }, [schedule]);
  return shown;
}

/**
 * The full /id record for whichever schedule is open.
 *
 * Cached per id for as long as the panel is mounted. Each call is two round trips —
 * schedulerRequest mints a service token before every request — and within one visit to
 * the list the record cannot change underneath us: editing navigates away and remounts
 * the page, and deleting closes the panel.
 *
 * `byId` in the dependency list is honest for exhaustive-deps and cannot loop: the
 * `byId[id]` guard returns immediately on the re-run its own setState causes. Retry works
 * by deleting the entry, which is what lets the effect fire again.
 */
function useScheduleDetail(id) {
  const [byId, setById] = useState({});

  useEffect(() => {
    if (!id || byId[id]) return undefined;

    let cancelled = false;
    fetchScheduleById(id)
      .then((record) => {
        if (cancelled) return;
        // A null Result is no more use than a thrown error - there is no record either way
        setById((map) => ({ ...map, [id]: record ? { record } : { error: true } }));
      })
      .catch((error) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[scheduler] could not load the schedule detail for the summary panel", error);
        setById((map) => ({ ...map, [id]: { error: true } }));
      });

    return () => {
      cancelled = true;
    };
  }, [id, byId]);

  const retry = useCallback((target) => {
    setById((map) => {
      const next = { ...map };
      delete next[target];
      return next;
    });
  }, []);

  return { byId, retry };
}

export default function ScheduleSummaryPanel({ open, schedule, onClose, onEdit }) {
  const held = useLastSchedule(schedule);

  /* Two different ids, and that is the whole trick for not fighting the slide-out.

     The fetch is keyed on the live prop, so only what is actually open is requested and
     the cleanup cancels anything in flight when the user closes or switches row. The
     render reads the held row, so during the slide-out `schedule` is null while `held`
     and its cached record are both still there — the panel stays complete all the way
     out, with no extra state and no spinner stuck on mid-animation. */
  const { byId, retry } = useScheduleDetail(schedule?.id);
  const entry = held?.id ? byId[held.id] : null;

  const detailState = !held ? "idle" : !entry ? "loading" : entry.error ? "error" : "ready";
  const pending = detailState === "loading";

  /* The row rebuilt from the merged record, so every derived field — frequency, the
     timezone spelling split, the status fallback — is normalised once by rowFromSchedule
     rather than patched field by field here. Falls back to the list's row, which is what
     makes a failed fetch a degradation rather than an empty panel. */
  const shown = useMemo(() => {
    const full = entry && entry.record;
    return full && held ? rowFromSchedule(mergeScheduleRecords(held.raw, full)) : held;
  }, [held, entry]);

  /* Memoised because CalendarMonth resets its month view whenever the identity of
     upcomingDates changes. A fresh array each render would snap the grid back to the
     first month on every re-render and make paging forward impossible. */
  const upcoming = useMemo(() => (shown?.raw ? upcomingRunDates(shown.raw) : []), [shown]);

  const statusKey = String(shown?.status || "").toLowerCase();

  /* Only a field that is still a dash gets the placeholder, and only while the fetch is
     actually running — so the day /all returns these, it disappears on its own. Without
     it a dash reads as "this schedule has no time zone" rather than "we have not looked
     yet". */
  const detail = (value) =>
    pending && value === NOT_RETURNED ? <span className="sspanel-pending">Loading…</span> : value;

  const fields = shown
    ? [
        ["Report Name", shown.reportName],
        ["Frequency", shown.frequency],
        ["File Type", detail(shown.fileType)],
        ["Schedule Type", detail(shown.scheduleType)],
        ["Scheduled Date & Time", shown.scheduledDateTime],
        ["Time Zone", detail(shown.timeZone)],
        ["Status", <span className={`sspanel-status sspanel-status-${statusKey}`}>{shown.status}</span>],
        ["Created By", detail(shown.createdBy)],
        [
          "Users",
          <ul className="sspanel-users">
            {(shown.recipients || []).map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>,
        ],
      ]
    : [];

  return (
    <Drawer
      className="sspanel-drawer"
      visible={open}
      onClose={onClose}
      placement="right"
      width={420}
      title={<span className="sspanel-title">Summary</span>}
      closeIcon={Icon.close}
      headerStyle={{ padding: "16px 20px", borderBottom: "1px solid #eef2f7" }}
      bodyStyle={{ padding: "6px 20px 20px" }}
      footerStyle={{ padding: "14px 20px 18px", borderTop: "1px solid #eef2f7" }}
      footer={
        <button type="button" className="sspanel-edit-btn" onClick={() => onEdit?.(shown)}>
          {Icon.edit}
          Edit
        </button>
      }
    >
      {shown && (
        <>
          {fields.map(([label, value]) => (
            <div className="sspanel-row" key={label}>
              <span className="sspanel-label">{label}</span>
              <span className="sspanel-value">{value}</span>
            </div>
          ))}

          <div className="sspanel-section-head">
            Upcoming Schedules
            {Icon.calendar}
          </div>

          {/* Three states, because two of them used to read as the third. While the fetch
              was in flight this said "No upcoming runs for this schedule." — a claim we
              had not yet checked — and a failed fetch said the same thing.

              The calendar is gated on the fetch rather than drawn from the partial record
              first, so the grid does not appear and then silently change its marks a
              moment later. */}
          {pending ? (
            <div className="sspanel-calendar">
              <div className="sspanel-cal-empty">
                <Spin size="small" /> <span>Checking upcoming runs…</span>
              </div>
            </div>
          ) : upcoming.length > 0 ? (
            <CalendarMonth upcomingDates={upcoming} />
          ) : (
            <div className="sspanel-calendar">
              <div className="sspanel-cal-empty">No upcoming runs for this schedule.</div>
            </div>
          )}

          {/* The dates above are still right for anything this app created — /all does
              return Recurrence, so the Ends block is in hand. Only a legacy schedule
              relying on ValidTo or ExecutionLimit loses its end date, which is why this
              says "partial" rather than claiming the calendar is wrong.

              No toast: this is a passive read on a panel someone opened to look at, and a
              Retry where they are already looking beats a message in the corner. */}
          {detailState === "error" ? (
            <div className="sspanel-cal-note">
              <span>Showing partial details.</span>
              <button type="button" className="sspanel-retry" onClick={() => retry(held.id)}>
                Retry
              </button>
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
